/**
 * Challenge Utilities
 * Handles daily challenge rotation and progress tracking
 */

import { ChallengeId, CHALLENGES, DailyChallengeSet, UserChallenge } from '../types/challenges';

/**
 * Get today's date string in YYYY-MM-DD format
 */
export function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Generate a deterministic pseudo-random challenge set for a given date
 * Uses date as seed for consistent daily challenges
 */
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Get the challenge IDs for a specific date
 * Returns 3 random challenges based on the date
 */
export function getChallengesForDate(date: string): ChallengeId[] {
  const challengeIds = Object.keys(CHALLENGES) as ChallengeId[];
  
  // Convert date to a numeric seed
  const seed = date.split('-').reduce((acc, part) => acc + parseInt(part, 10), 0);
  
  // Use Fisher-Yates shuffle with seeded random
  const shuffled = [...challengeIds];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Return first 3 challenges
  return shuffled.slice(0, 3);
}

/**
 * Get today's challenge set
 */
export function getTodaysChallenges(): DailyChallengeSet {
  const today = getTodayString();
  return {
    date: today,
    challenges: getChallengesForDate(today),
  };
}

/**
 * Check if a challenge is completed based on session result
 */
export function checkChallengeProgress(
  challengeId: ChallengeId,
  sessionResult: {
    wordsRead: number;
    avgWpm: number;
    durationSeconds: number;
  },
  currentProgress: number,
  currentStreak: number
): { completed: boolean; newProgress: number } {
  const challenge = CHALLENGES[challengeId];
  if (!challenge) {
    return { completed: false, newProgress: currentProgress };
  }

  let progress = currentProgress;
  let completed = false;

  switch (challengeId) {
    case 'wordSprint': {
      // Check if single session had 500+ words
      if (sessionResult.wordsRead >= challenge.requirement) {
        progress = Math.max(progress, sessionResult.wordsRead);
        completed = progress >= challenge.requirement;
      }
      break;
    }
    case 'speedRun': {
      // Check if session achieved 400+ WPM
      if (sessionResult.avgWpm >= challenge.requirement) {
        completed = true;
      }
      break;
    }
    case 'nightOwl': {
      const hour = new Date().getHours();
      if (hour >= 22 || hour < 5) {
        completed = true;
      }
      break;
    }
    case 'earlyBird': {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 7) {
        completed = true;
      }
      break;
    }
    case 'volumeKing': {
      // Accumulate words read today
      progress = currentProgress + sessionResult.wordsRead;
      completed = progress >= challenge.requirement;
      break;
    }
    case 'streakDefender': {
      // Check if streak is 7+ days
      progress = currentStreak;
      completed = currentStreak >= challenge.requirement;
      break;
    }
  }

  return { completed, newProgress: progress };
}

/**
 * Get challenge completion percentage
 */
export function getChallengeProgressPercent(challengeId: ChallengeId, progress: number): number {
  const challenge = CHALLENGES[challengeId];
  if (!challenge) return 0;
  
  if (challengeId === 'speedRun' || challengeId === 'nightOwl' || challengeId === 'earlyBird') {

    return progress    // Binary challenges > 0 ? 100 : 0;
  }
  
  return Math.min(100, Math.round((progress / challenge.requirement) * 100));
}

/**
 * Format challenge progress for display
 */
export function formatChallengeProgress(challengeId: ChallengeId, progress: number): string {
  const challenge = CHALLENGES[challengeId];
  if (!challenge) return '';
  
  if (challengeId === 'speedRun' || challengeId === 'nightOwl' || challengeId === 'earlyBird') {
    return progress > 0 ? '✓ Complete' : '0/1';
  }
  
  return `${Math.min(progress, challenge.requirement)}/${challenge.requirement} ${challenge.unit}`;
}

/**
 * Get challenge time remaining until reset
 */
export function getTimeUntilChallengeReset(): { hours: number; minutes: number } {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  
  const diff = tomorrow.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return { hours, minutes };
}
